import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { 
  getDocumentationById, 
  createDocumentation, 
  updateDocumentation,
  getDocumentationSections,
  uploadDocumentationScreenshot,
  deleteDocumentationScreenshot,
  getScreenshotUrl
} from '../../../models/documentationModel';
import WysiwygEditor from '../../../components/common/WysiwygEditor';
import MultiFileInput from '../../../components/common/MultiFileInput';
import { API_BASE_URL } from '../../../config';

// Common route paths for quick selection
const COMMON_PATHS = [
  { path: '/dashboard', label: 'Dashboard Home' },
  { path: '/dashboard/requisitions', label: 'Requisitions' },
  { path: '/dashboard/specifications', label: 'Specifications' },
  { path: '/dashboard/rfqs', label: 'RFQs' },
  { path: '/dashboard/procurement', label: 'Procurement' },
  { path: '/dashboard/helpdesk', label: 'Helpdesk' },
  { path: '/dashboard/mms', label: 'MMS' },
  { path: '/dashboard/assets', label: 'Assets' },
  { path: '/dashboard/users', label: 'Users' },
  { path: '/dashboard/departments', label: 'Departments' },
  { path: '/dashboard/sites', label: 'Sites' },
  { path: '/dashboard/roles', label: 'Roles' },
  { path: '/dashboard/categories', label: 'Categories' },
  { path: '/dashboard/budgets', label: 'Budgets' },
  { path: '/dashboard/reports', label: 'Reports' },
  { path: '/dashboard/settings', label: 'Settings' },
  { path: '/dashboard/tanker', label: 'Water Tanker' },
];

const DOC_TYPES = [
  { value: 'page', label: 'Documentation Page' },
  { value: 'tooltip', label: 'Tooltip' },
  { value: 'walkthrough', label: 'Walkthrough/Tutorial' },
  { value: 'faq', label: 'FAQ' }
];

function DocumentationEditor() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isEditing = Boolean(id);
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sections, setSections] = useState([]);
  const [screenshots, setScreenshots] = useState([]); // Existing screenshots from server
  const [pendingFiles, setPendingFiles] = useState([]); // New files to upload
  const [uploadingScreenshot, setUploadingScreenshot] = useState(false);
  
  // Get initial values from URL query params (for new docs)
  const initialPath = searchParams.get('path') || '';
  const initialType = searchParams.get('type') || 'page';
  
  const [formData, setFormData] = useState({
    doc_title: '',
    doc_path: initialPath,
    doc_path_pattern: '',
    doc_section: '',
    doc_content: '',
    doc_summary: '',
    doc_keywords: '',
    doc_order: 0,
    doc_status: 1,
    doc_type: initialType,
    doc_icon: '',
    doc_video_url: ''
  });
  
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchSections();
    if (isEditing) {
      fetchDocumentation();
    }
  }, [id]);

  const fetchSections = async () => {
    try {
      const response = await getDocumentationSections();
      if (response.status === 1 || response.status === 'success') {
        setSections(response.data?.sections || []);
      }
    } catch (error) {
      console.error('Error fetching sections:', error);
    }
  };

  const fetchDocumentation = async () => {
    try {
      setLoading(true);
      const response = await getDocumentationById(id);
      if (response.status === 1 || response.status === 'success') {
        const doc = response.data?.documentation;
        if (doc) {
          setFormData({
            doc_title: doc.doc_title || '',
            doc_path: doc.doc_path || '',
            doc_path_pattern: doc.doc_path_pattern || '',
            doc_section: doc.doc_section || '',
            doc_content: doc.doc_content || '',
            doc_summary: doc.doc_summary || '',
            doc_keywords: doc.doc_keywords || '',
            doc_order: doc.doc_order || 0,
            doc_status: doc.doc_status ?? 1,
            doc_type: doc.doc_type || 'page',
            doc_icon: doc.doc_icon || '',
            doc_video_url: doc.doc_video_url || ''
          });
          setScreenshots(doc.screenshots || []);
        }
      }
    } catch (error) {
      console.error('Error fetching documentation:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.doc_title.trim()) {
      newErrors.doc_title = 'Title is required';
    }
    if (!formData.doc_path.trim()) {
      newErrors.doc_path = 'Path is required';
    } else if (!formData.doc_path.startsWith('/')) {
      newErrors.doc_path = 'Path must start with /';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) {
      toast.error('Please fix the errors before saving');
      return;
    }
    
    try {
      setSaving(true);
      
      let response;
      let docId = id;
      
      if (isEditing) {
        response = await updateDocumentation(id, formData);
      } else {
        response = await createDocumentation(formData);
        // Get the new document ID for uploading screenshots
        if (response.status === 1 || response.status === 'success') {
          docId = response.data?.documentation?.doc_id || response.data?.id;
        }
      }
      
      if (response.status === 1 || response.status === 'success') {
        // Upload any pending files
        if (pendingFiles.length > 0 && docId) {
          setUploadingScreenshot(true);
          let uploadedCount = 0;
          for (const file of pendingFiles) {
            try {
              await uploadDocumentationScreenshot(docId, file);
              uploadedCount++;
            } catch (uploadError) {
              console.error('Error uploading screenshot:', uploadError);
              toast.warning(`Failed to upload ${file.name}`);
            }
          }
          if (uploadedCount > 0) {
            toast.info(`${uploadedCount} screenshot(s) uploaded`);
          }
          setUploadingScreenshot(false);
        }
        
        toast.success(isEditing ? 'Documentation updated successfully' : 'Documentation created successfully');
        navigate('/dashboard/documentation');
      } else {
        toast.error(response.message || 'Error saving documentation');
        console.error('Error saving:', response.message);
      }
    } catch (error) {
      console.error('Error saving documentation:', error);
      toast.error('Failed to save documentation');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteScreenshot = async (screenshotId) => {
    if (!window.confirm('Are you sure you want to delete this screenshot?')) return;
    
    try {
      const response = await deleteDocumentationScreenshot(screenshotId);
      if (response.status === 1 || response.status === 'success') {
        setScreenshots(prev => prev.filter(s => s.screenshot_id !== screenshotId));
        toast.success('Screenshot deleted');
      } else {
        toast.error('Failed to delete screenshot');
      }
    } catch (error) {
      console.error('Error deleting screenshot:', error);
      toast.error('Failed to delete screenshot');
    }
  };

  const insertScreenshotIntoContent = (screenshot) => {
    const imageUrl = `${API_BASE_URL}/api/documentation/screenshots/download/${screenshot.screenshot_filename}`;
    const imageHtml = `<p><img src="${imageUrl}" alt="${screenshot.screenshot_caption || screenshot.screenshot_original_name}" style="max-width: 100%; height: auto;" /></p>`;
    
    handleChange('doc_content', formData.doc_content + imageHtml);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="container mx-auto px-4 py-4">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/dashboard/documentation')}
                className="mr-4 p-2 hover:bg-gray-100 rounded-lg"
              >
                <svg className="h-5 w-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-800">
                  {isEditing ? 'Edit Documentation' : 'Create Documentation'}
                </h1>
                <p className="text-xs text-gray-500">
                  {isEditing ? 'Update existing documentation page' : 'Create a new context-aware documentation page'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate('/dashboard/documentation')}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={saving}
                className="px-4 py-2 rounded-md text-sm font-medium text-white disabled:opacity-50"
                style={{ backgroundColor: '#0087D4' }}
              >
                {saving ? 'Saving...' : (isEditing ? 'Update' : 'Create')}
              </button>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-4">
              {/* Basic Info */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-800 mb-4">Basic Information</h2>
                
                <div className="space-y-4">
                  {/* Title */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Title <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.doc_title}
                      onChange={(e) => handleChange('doc_title', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md text-gray-900 ${errors.doc_title ? 'border-red-500' : 'border-gray-300'}`}
                      placeholder="Enter documentation title"
                    />
                    {errors.doc_title && <p className="mt-1 text-xs text-red-500">{errors.doc_title}</p>}
                  </div>
                  
                  {/* Path */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Path <span className="text-red-500">*</span>
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={formData.doc_path}
                        onChange={(e) => handleChange('doc_path', e.target.value)}
                        className={`flex-1 px-3 py-2 border rounded-md text-gray-900 ${errors.doc_path ? 'border-red-500' : 'border-gray-300'}`}
                        placeholder="/dashboard/module"
                      />
                      <select
                        onChange={(e) => {
                          if (e.target.value) {
                            handleChange('doc_path', e.target.value);
                          }
                        }}
                        value=""
                        className="px-3 py-2 border border-gray-300 rounded-md text-sm"
                      >
                        <option value="">Quick select...</option>
                        {COMMON_PATHS.map(p => (
                          <option key={p.path} value={p.path}>{p.label}</option>
                        ))}
                      </select>
                    </div>
                    {errors.doc_path && <p className="mt-1 text-xs text-red-500">{errors.doc_path}</p>}
                    <p className="mt-1 text-xs text-gray-500">
                      The route path where this documentation will be shown (e.g., /dashboard/requisitions)
                    </p>
                  </div>
                  
                  {/* Summary */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Summary</label>
                    <textarea
                      value={formData.doc_summary}
                      onChange={(e) => handleChange('doc_summary', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900"
                      rows={2}
                      placeholder="Brief summary shown in the help tooltip"
                    />
                  </div>
                </div>
              </div>

              {/* Content Editor */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-800 mb-4">Content</h2>
                <WysiwygEditor
                  value={formData.doc_content}
                  onChange={(value) => handleChange('doc_content', value)}
                  placeholder="Write your documentation content here..."
                  minHeight="400px"
                />
              </div>

              {/* Screenshots */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-800 mb-4">Screenshots & Attachments</h2>
                
                {/* MultiFileInput for new uploads */}
                <MultiFileInput
                  files={pendingFiles}
                  onChange={setPendingFiles}
                  accept="image/*"
                  multiple={true}
                  className="mb-4"
                />
                
                {/* Existing Screenshots (only when editing) */}
                {isEditing && screenshots.length > 0 && (
                  <div className="mt-4">
                    <h3 className="text-sm font-medium text-gray-700 mb-2">
                      Existing Screenshots ({screenshots.length})
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {screenshots.map(screenshot => (
                        <div key={screenshot.screenshot_id} className="relative group">
                          <img
                            src={`${API_BASE_URL}/api/documentation/screenshots/download/${screenshot.screenshot_filename}`}
                            alt={screenshot.screenshot_caption || screenshot.screenshot_original_name}
                            className="w-full h-32 object-cover rounded-lg border border-gray-200"
                          />
                          <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-2">
                            <button
                              type="button"
                              onClick={() => insertScreenshotIntoContent(screenshot)}
                              className="p-1.5 bg-white rounded-md text-blue-600 hover:bg-blue-50"
                              title="Insert into content"
                            >
                              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                              </svg>
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteScreenshot(screenshot.screenshot_id)}
                              className="p-1.5 bg-white rounded-md text-red-600 hover:bg-red-50"
                              title="Delete"
                            >
                              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                          {screenshot.screenshot_caption && (
                            <p className="text-xs text-gray-500 mt-1 truncate">{screenshot.screenshot_caption}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {!isEditing && pendingFiles.length === 0 && (
                  <p className="text-sm text-gray-500 text-center py-2">
                    Screenshots will be uploaded when you save the documentation.
                  </p>
                )}
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-4">
              {/* Settings */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-800 mb-4">Settings</h2>
                
                <div className="space-y-4">
                  {/* Status */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <select
                      value={formData.doc_status}
                      onChange={(e) => handleChange('doc_status', parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900"
                    >
                      <option value={1}>Active</option>
                      <option value={0}>Draft</option>
                    </select>
                  </div>
                  
                  {/* Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                    <select
                      value={formData.doc_type}
                      onChange={(e) => handleChange('doc_type', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900"
                    >
                      {DOC_TYPES.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                  </div>
                  
                  {/* Section */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Section</label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={formData.doc_section}
                        onChange={(e) => handleChange('doc_section', e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-gray-900"
                        placeholder="Enter or select section"
                        list="sections-list"
                      />
                      <datalist id="sections-list">
                        {sections.map(s => (
                          <option key={s} value={s} />
                        ))}
                        <option value="General" />
                        <option value="Procurement" />
                        <option value="IT" />
                        <option value="HR" />
                        <option value="Finance" />
                        <option value="MMS" />
                      </datalist>
                    </div>
                  </div>
                  
                  {/* Order */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Display Order</label>
                    <input
                      type="number"
                      value={formData.doc_order}
                      onChange={(e) => handleChange('doc_order', parseInt(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900"
                      min="0"
                    />
                    <p className="mt-1 text-xs text-gray-500">Lower numbers appear first</p>
                  </div>
                </div>
              </div>

              {/* Advanced */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-800 mb-4">Advanced</h2>
                
                <div className="space-y-4">
                  {/* Keywords */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Keywords</label>
                    <input
                      type="text"
                      value={formData.doc_keywords}
                      onChange={(e) => handleChange('doc_keywords', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900"
                      placeholder="comma, separated, keywords"
                    />
                    <p className="mt-1 text-xs text-gray-500">For search optimization</p>
                  </div>
                  
                  {/* Video URL */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Video Tutorial URL</label>
                    <input
                      type="url"
                      value={formData.doc_video_url}
                      onChange={(e) => handleChange('doc_video_url', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900"
                      placeholder="https://youtube.com/..."
                    />
                  </div>
                  
                  {/* Path Pattern */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Path Pattern (Regex)</label>
                    <input
                      type="text"
                      value={formData.doc_path_pattern}
                      onChange={(e) => handleChange('doc_path_pattern', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900"
                      placeholder="^/dashboard/requisitions/\d+$"
                    />
                    <p className="mt-1 text-xs text-gray-500">Optional regex for matching multiple paths</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

export default DocumentationEditor;
